import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import './DashboardHome.css';
import { DailyReportDto, Zodiac, DailyMeditationDto } from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, Battery, Bell, Calendar, CheckCircle, ChevronDown, ChevronUp, Heart, Moon, Play, RotateCcw, Sparkles, Star, Sun } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import ErrorRetryCard from './ErrorRetryCard';
import { Button } from './ui/button';
import MeditationPlayer from './meditation/MeditationPlayer';
import {
  buildMeditationPhases,
  formatSessionTime,
  totalMeditationDurationSeconds,
} from './meditation/meditationSession';

const DashboardHome: React.FC = () => {
  const history = useHistory();
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const api = useAstroApiClient();

  const formatPlanetInSign = (planetKey: string, sign: Zodiac | null) => {
    if (!sign) {
      return t(planetKey);
    }

    return t('dashboardPlanetInSign', {
      planet: t(planetKey),
      sign: t('sign_' + sign),
    });
  };

  const goError = () => {
    history.push("/error");
  };

  const [sunSign, setSunSign] = useState<Zodiac | null>(null);
  const [moonSignState, setMoonSignState] = useState<Zodiac | null>(null);
  const [lunarPhase, setLunarPhase] = useState<string>('');
  const [phaseMessage, setPhaseMessage] = useState<string>('');
  const [dailyMessage, setDailyMessage] = useState<string>('');
  const [venusSign, setVenusSign] = useState<Zodiac | null>(null);
  const [marsSign, setMarsSign] = useState<Zodiac | null>(null);
  const [venusMessage, setVenusMessage] = useState<string>('');
  const [marsMessage, setMarsMessage] = useState<string>('');
  const [moonMessage, setMoonMessage] = useState<string>('');
  const [retrogradePlanets, setRetrogradePlanets] = useState<string[]>([]);
  const [dailyEnergy, setDailyEnergy] = useState<number | null>(null);
  const [meditation, setMeditation] = useState<DailyMeditationDto | null>(null);
  const [isMeditationPlayerOpen, setIsMeditationPlayerOpen] = useState<boolean>(false);
  const [meditationTextExpanded, setMeditationTextExpanded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    solar: false,
    lunar: false,
    love: false,
    energy: false,
  });

  // Track if content overflows (to decide showing the expand button)
  const [overflowing, setOverflowing] = useState<Record<string, boolean>>({
    solar: false,
    lunar: false,
    love: false,
    energy: false,
  });

  const solarRef = React.useRef<HTMLElement | null>(null);
  const lunarRef = React.useRef<HTMLElement | null>(null);
  const loveRef = React.useRef<HTMLElement | null>(null);
  const energyRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    const compute = () => {
      const hasOverflow = (el: HTMLElement | null) => !!el && el.scrollHeight > el.clientHeight + 1;
      setOverflowing({
        solar: hasOverflow(solarRef.current),
        lunar: hasOverflow(lunarRef.current),
        love: hasOverflow(loveRef.current),
        energy: hasOverflow(energyRef.current),
      });
    };

    compute();

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(compute);
      [solarRef.current, lunarRef.current, loveRef.current, energyRef.current]
        .forEach(el => { if (el) ro!.observe(el); });
    }

    const onResize = () => compute();
    window.addEventListener('resize', onResize);

    // Recompute after next paint in case fonts/layout settle
    const raf = requestAnimationFrame(compute);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
    // Dependencies include text that can change height
  }, [dailyMessage, phaseMessage, sunSign, lunarPhase]);

  const toggle = (key: keyof typeof expanded) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const fetchRetrogrades = React.useCallback(async () => {
    try {
      const retrogrades = await api.apiAstrologyRetrogrades();
      setRetrogradePlanets(retrogrades ?? []);
    } catch {
      // Dados secundarios: nao bloqueiam a vista principal.
      setRetrogradePlanets([]);
    }
  }, [api]);

  const fetchEnergy = React.useCallback(async () => {
    try {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      const keys = ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'];

      const transits = await api.apiAstrologyTransitReadingsMe(dateStr, keys.join(','));
      if (transits?.summaries) {
        const vals = transits.summaries
          .filter(s => keys.includes(s.profileKey ?? ''))
          .map(s => s.transitEvaluation?.percentAbsolute ?? 0);
        if (vals.length > 0) {
          const avg = Math.round(vals.reduce((a, b) => a + Number(b), 0) / vals.length);
          setDailyEnergy(avg);
        }
      }
    } catch {
      // Dados secundarios: nao bloqueiam a vista principal.
    }
  }, [api]);

  // Relatorio diario primario. A falha deixa de ser ignorada: marca loadError
  // para mostrar o card de erro com retry, mantendo o skeleton de loading.
  const loadReport = React.useCallback(async () => {
    if (!auth.user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(false);

    let report: DailyReportDto | undefined;
    try {
      report = await api.apiAstrologyDailyReportMe();
    } catch {
      setLoadError(true);
      setLoading(false);
      return;
    }

    if (!report) {
      setLoading(false);
      return;
    }

    setSunSign(report.sunSign ?? null);
    setMoonSignState(report.moonSign ?? null);
    setLunarPhase(report.lunarPhase ?? '');
    setPhaseMessage(report.phaseMessage ?? '');
    setDailyMessage(report.message ?? '');
    setVenusSign(report.venusSign ?? null);
    setMarsSign(report.marsSign ?? null);
    setVenusMessage(report.venusMessage ?? '');
    setMarsMessage(report.marsMessage ?? '');
    setMoonMessage(report.moonMessage ?? '');
    setMeditation(report.meditation ?? null);

    // Dados secundarios (retrogrados, energia) nao bloqueiam a vista principal.
    await Promise.allSettled([fetchRetrogrades(), fetchEnergy()]);
    setLoading(false);
  }, [auth.user, api, fetchRetrogrades, fetchEnergy]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  // MI-005: a entrada para a sessao usa o mesmo modelo do player, para que a
  // duracao anunciada no cartao seja exatamente a que a sessao vai executar.
  const meditationPhases = React.useMemo(() => buildMeditationPhases(meditation), [meditation]);
  const meditationTotalSeconds = React.useMemo(
    () => totalMeditationDurationSeconds(meditationPhases),
    [meditationPhases],
  );
  const canPlayMeditation = meditationPhases.length > 0;

  if (loading) {
    return (
      <div className="space-y-4 md:space-y-6" aria-busy="true">
        <div className="space-y-2">
          <div className="h-6 w-48 animate-pulse rounded bg-stone-200" />
          <div className="h-3 w-64 animate-pulse rounded bg-stone-200" />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Card key={index} className="border-stone-200 bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="h-4 w-24 animate-pulse rounded bg-stone-200" />
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="h-7 w-20 animate-pulse rounded bg-stone-200" />
                <div className="h-3 w-28 animate-pulse rounded bg-stone-100" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1].map((index) => (
            <Card key={index} className="border-stone-200 bg-white shadow-sm">
              <CardHeader>
                <div className="h-5 w-40 animate-pulse rounded bg-stone-200" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="h-3 w-full animate-pulse rounded bg-stone-100" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-stone-100" />
                <div className="h-3 w-3/4 animate-pulse rounded bg-stone-100" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <ErrorRetryCard
        message={t('dashboardLoadError')}
        retryLabel={t('insightsRetry')}
        onRetry={loadReport}
      />
    );
  }

  // Blocos de texto das fases. O cartao mostra o primeiro como pre-visualizacao
  // e revela os restantes a pedido: o texto continua acessivel sem abrir o player.
  const meditationBlocks: { key: string; node: React.ReactNode }[] = [];

  if (meditation?.preparation) {
    meditationBlocks.push({
      key: 'preparation',
      node: (
        <div className="p-3 bg-white/70 rounded-lg border border-indigo-100">
          <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
            <Moon className="h-4 w-4 mr-2 text-indigo-400" />
            {meditation.preparation.title}
          </p>
          {meditation.preparation.focus && (
            <p className="text-xs text-slate-600 mb-1 leading-relaxed">
              {meditation.preparation.focus}
            </p>
          )}
          <p className="text-xs text-slate-700 leading-relaxed">
            {meditation.preparation.text}
          </p>
        </div>
      ),
    });
  }

  if (meditation?.breathing) {
    meditationBlocks.push({
      key: 'breathing',
      node: (
        <div className="p-3 bg-white/70 rounded-lg border border-indigo-100">
          <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
            <Sun className="h-4 w-4 mr-2 text-amber-400" />
            {meditation.breathing.title}
          </p>
          {meditation.breathing.focus && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">{meditation.breathing.focus}</span>
            </div>
          )}
          <p className="text-xs text-slate-700 leading-relaxed">
            {meditation.breathing.text}
          </p>
        </div>
      ),
    });
  }

  if (meditation?.intention) {
    meditationBlocks.push({
      key: 'intention',
      node: (
        <div className="p-3 bg-white/70 rounded-lg border border-indigo-100">
          <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
            <Star className="h-4 w-4 mr-2 text-yellow-400" />
            {meditation.intention.title}
          </p>
          <p className="text-xs text-slate-700 leading-relaxed italic">
            {meditation.intention.text}
          </p>
          {meditation.intention.focus && (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {meditation.intention.focus}
            </p>
          )}
        </div>
      ),
    });
  }

  if (meditation?.closing) {
    meditationBlocks.push({
      key: 'closing',
      node: (
        <div className="p-3 bg-white/70 rounded-lg border border-indigo-100">
          <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
            <Heart className="h-4 w-4 mr-2 text-rose-400" />
            {meditation.closing.title}
          </p>
          <p className="text-xs text-slate-700 leading-relaxed font-medium italic mt-1">
            {meditation.closing.text}
          </p>
          {meditation.closing.focus && (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              {meditation.closing.focus}
            </p>
          )}
        </div>
      ),
    });
  }

  const visibleMeditationBlocks = meditationTextExpanded
    ? meditationBlocks
    : meditationBlocks.slice(0, 1);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('dashboardHomeTitle')}</h2>
          <p className="mt-1 text-sm text-slate-600">{t('dashboardHomeSubtitle')}</p>
        </div>
        <div className="rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
          <span className="font-medium">{new Date().toLocaleDateString(i18n.language, { day: '2-digit', month: 'short' })}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center">
              <Sun className="h-4 w-4 mr-2 text-amber-500" />
              {t('solarSign')}
            </CardTitle>
          </CardHeader>
          <CardContent ref={solarRef as any}>
            <div className="text-2xl font-bold text-slate-800">{sunSign ? t('sign_' + sunSign) : '-'}</div>
            <p className="text-xs text-slate-600 mt-1">{dailyMessage || '...'}</p>
          </CardContent>
        </Card>
        <Card className="border-sky-200 bg-gradient-to-br from-blue-50 to-sky-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-sky-700 flex items-center">
              <Moon className="h-4 w-4 mr-2 text-sky-600" />
              {t('lunarPhase')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{lunarPhase ? t('phase_' + lunarPhase) : '-'}</div>
            <p className="text-xs text-sky-700 mt-1">{phaseMessage || '...'}</p>
          </CardContent>
        </Card>

        {(() => {
          const hasRetrograde = retrogradePlanets.length > 0
          return (
            <Card className={`border shadow-sm transition-shadow hover:shadow-md ${hasRetrograde ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300" : "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200"}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm font-medium flex items-center ${hasRetrograde ? "text-amber-800" : "text-emerald-700"}`}>
                  {hasRetrograde ? (
                    <RotateCcw className="h-4 w-4 mr-2 text-amber-600" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" />
                  )}
                  {t('dashboardRetrogradesTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {hasRetrograde ? (
                  <>
                    <div className="flex items-center gap-1.5 mb-1">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                      <span className="text-xs font-semibold text-amber-800">
                        {t('dashboardRetrogradeCount', { count: retrogradePlanets.length })}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {retrogradePlanets.map((p) => (
                        <span
                          key={p}
                          className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5"
                        >
                          {t('planet_' + p)}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-amber-700 mt-1.5">{t('dashboardRetrogradeReview')}</p>
                  </>
                ) : (
                  <>
                    <div className="text-xl md:text-2xl font-bold text-emerald-700">{t('dashboardNone')}</div>
                    <p className="text-xs text-emerald-600 mt-1">{t('dashboardDirectEnergyToday')}</p>
                  </>
                )}
              </CardContent>
            </Card>
          )
        })()}

        <Card className="border-green-200 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-green-700 flex items-center">
              <Battery className="h-4 w-4 mr-2 text-green-600" />
              {t('insightsEnergyTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-800">{dailyEnergy !== null ? `${dailyEnergy}%` : '-'}</div>
            <p className="text-xs text-green-700 mt-1">{t('insightsEnergyOverall')}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="bg-white border-stone-200 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader>
            <CardTitle className="text-slate-800 flex items-center">
              <Sparkles className="h-5 w-5 mr-2 text-purple-400" />
              {t('todaysTransits')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
              <div>
                <p className="text-slate-800 font-medium">{formatPlanetInSign('planet_Venus', venusSign)}</p>
                <p className="text-sm text-slate-600">{venusMessage || '...'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
              <div>
                <p className="text-slate-800 font-medium">{formatPlanetInSign('planet_Mars', marsSign)}</p>
                <p className="text-sm text-slate-600">{marsMessage || '...'}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
              <div>
                <p className="text-slate-800 font-medium">{formatPlanetInSign('planet_Moon', moonSignState)}</p>
                <p className="text-sm text-slate-600">{moonMessage || '...'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200 shadow-sm transition-shadow hover:shadow-md">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-base md:text-lg text-slate-800 flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-indigo-500" />
                  {meditation?.title || t('dashboardMeditationOfDay')}
                </CardTitle>
                <p className="text-xs text-indigo-600 mt-1">{meditation?.subtitle || t('dashboardMeditationSubtitle')}</p>
              </div>
              {canPlayMeditation && (
                <div className="flex flex-col items-start gap-1 sm:items-end">
                  <Button size="sm" onClick={() => setIsMeditationPlayerOpen(true)}>
                    <Play className="h-4 w-4" />
                    {t('dashboardMeditationStartSession')}
                  </Button>
                  {meditationTotalSeconds !== null && (
                    <span className="text-xs text-indigo-600">
                      {t('meditationPlayerTotalDuration', {
                        time: formatSessionTime(meditationTotalSeconds),
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {meditation ? (
              <>
                {visibleMeditationBlocks.map((block) => (
                  <React.Fragment key={block.key}>{block.node}</React.Fragment>
                ))}
                {meditationBlocks.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-indigo-700"
                    aria-expanded={meditationTextExpanded}
                    onClick={() => setMeditationTextExpanded((value) => !value)}
                  >
                    {meditationTextExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    {meditationTextExpanded
                      ? t('dashboardMeditationCollapseText')
                      : t('dashboardMeditationReadFullText')}
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-indigo-600">{t('dashboardNoMeditationToday')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {canPlayMeditation && (
        <MeditationPlayer
          isOpen={isMeditationPlayerOpen}
          meditation={meditation}
          onClose={() => setIsMeditationPlayerOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardHome;


